import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';

const SPRITE_URL = 'assets/img/nav-icons-sprite.svg';
const CONTAINER_ID = 'nav-icons-sprite-container';

/**
 * Injects the new sidenav's SVG icon sprite inline into the document so the mine-ui
 * `<i mine-icon>` directive — which references symbols by bare fragment id (`#<name>-s`)
 * — can resolve the nav icons that are not (yet) part of the shared mine-ui remote
 * sprite. Mirrors mine-ui's own `MineSpriteService`: fetch once, inject into a hidden
 * container in `<body>`, guarded against double injection.
 *
 * `banner-s` is deliberately NOT in the local sprite — it already ships in the mine-ui
 * remote sprite, which the directive injects on its own. Remove this service once the
 * remaining icons ship in the mine-ui sprite (see app-nav CLAUDE.md).
 */
@Injectable({ providedIn: 'root' })
export class NavIconsSpriteService {
    private injected = false;

    constructor(@Inject(DOCUMENT) private document: Document) {}

    injectSprite(): void {
        if (this.injected || this.document.getElementById(CONTAINER_ID)) {
            return;
        }
        this.injected = true;
        fetch(SPRITE_URL)
            .then(res => res.text())
            .then(svg => this.appendSprite(svg))
            // Fail once: keep `injected = true` so a missing/5xx sprite doesn't trigger a fresh
            // fetch on every nav render. Icons fall back to the legacy CMS sprite.
            .catch(() => console.warn('NavIconsSpriteService: sprite unavailable, falling back to legacy icons.'));
    }

    private appendSprite(svg: string): void {
        if (this.document.getElementById(CONTAINER_ID)) {
            return;
        }
        const container = this.document.createElement('div');
        container.id = CONTAINER_ID;
        container.setAttribute('aria-hidden', 'true');
        container.style.cssText = 'position:absolute;width:0;height:0;overflow:hidden';
        container.innerHTML = svg;
        this.document.body.appendChild(container);
    }
}
